import React, { useCallback, useContext as useContextCore, useEffect, useRef, useState } from 'react'
import { useSearchParam } from '@ztechapps/common.hook'
import { FloatingActionButton } from '@ztechapps/webui.util'
import { ConversationWidgetPanel } from './ConversationWidgetPanel'
import { useMounted } from '../../hooks/useMounted'

export type ConversationWidgetContextType = {
  isOpen: boolean
  openConversationWidget: () => void
  closeConversationWidget: () => void
}

const context = React.createContext<ConversationWidgetContextType>({} as ConversationWidgetContextType)

export type ConversationWidgetProviderProps = { children: React.ReactNode }

export const ConversationWidgetProvider = ({ children }: ConversationWidgetProviderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  // The storefront renders every page through renderToStringWithData on the
  // server, so the compose panel only mounts after hydration on the client —
  // the same constraint the assistant bubble documents for its own mount.
  const mounted = useMounted()
  const [showHelpDesk] = useSearchParam('show-helpdesk')
  const launcherRef = useRef<HTMLButtonElement>(null)

  const openConversationWidget = useCallback(() => setIsOpen(true), [])
  const closeConversationWidget = useCallback(() => {
    setIsOpen(false)
    launcherRef.current?.focus({ preventScroll: true })
  }, [])

  // Support links land visitors straight in the docked panel through
  // `?show-helpdesk=true`; the link opens the docked panel on whatever page
  // they land on.
  useEffect(() => {
    if (showHelpDesk === 'true') openConversationWidget()
  }, [showHelpDesk, openConversationWidget])

  return (
    <context.Provider value={{ isOpen, openConversationWidget, closeConversationWidget }}>
      {children}
      {mounted ? (
        <>
          <FloatingActionButton
            icon="envelope"
            label="Contact Us"
            position="bottom-right"
            size="sm"
            onClick={openConversationWidget}
            buttonRef={launcherRef}
            dataTestId="conversation-widget-launcher"
          />
          <ConversationWidgetPanel />
        </>
      ) : null}
    </context.Provider>
  )
}

export const useConversationWidgetContext = () => useContextCore(context)
